import common from "@dotpe/rista-library/lib/common.js";
import { isEmptyArray, reduceToMap, getItemAttributes } from "./shared";
import store from "../../redux-store";
import { getLocalStorage } from "../../utils/localStorage";
import { LOCALSTORAGE_KEYS } from "../../utils/constants";
import { MILI_SECS_IN_ONE_DAY } from "../../utils/constants";
import { fetchCompleteCatalogData } from "../../redux-store/slices/LoginSlice";
import {
  getAllTablesFn,
  getCatalogueDataFn,
  getDiscountsFn,
  getItemsFn,
  getOptionSetsFn,
  getPrintersFn,
  getResourceFn,
  getResourceGroupsFn,
  isOffline,
} from "../../facade/ServiceFacade";

/**
 * `structuredClone` is declared locally because `packages/core`'s tsconfig has no `DOM` lib
 * (it must stay RN-safe); the global exists at runtime in every web/electron target. Same
 * treatment as `helpers/commonHelpers/index.desktop.ts`.
 */
declare function structuredClone<T>(value: T): T;

/**
 * WEB variant of `helper/catalogueHelpers`. Add further uplifted symbols here as they are
 * needed — see shared.ts for what is still left in the apps.
 */

/**
 * Returns `null` for empty input. Mobile returns `{}` — see index.native.ts.
 * Preserved, not reconciled: ~100 call sites, and web callers compensate with
 * `arrayToMap(...) || {}` / `?.[key]` in a way that assumes the nullable result.
 *
 * Return type is deliberately `any`: both apps' `catalogueHelpers.js` is plain JS, so every
 * consumer sees `any` today. A real type (`Record<string, any> | null`) would surface new
 * errors at call sites that never guarded — a typecheck change the uplift may not make.
 */
export function arrayToMap(array: any[], id: string): any {
  if (isEmptyArray(array)) {
    return null;
  }
  return reduceToMap(array, id);
}

/**
 * Builds `"<display key>" -> mid` for barcode/SKU search. Read by web `ItemsSlice`
 * (`setUpdatedItemsData` -> `channelItemsMidBarcodeMapping`).
 *
 * 🔴 **DIVERGENT — do not unify with index.native.ts.** Both platforms guard and iterate
 * identically; the KEY TEMPLATE differs, which is the whole function:
 *   web    `${shortName} ${attrs}_${longName} ${attrs}_#${sku}#_%${barCode}%`
 *   mobile `${shortName}_${longName}_#${sku}#_%${barCode}%`
 * Web folds the variant attributes in (via `getItemAttributes`) and separates them with a
 * SPACE; mobile has no `getItemAttributes` at all. The keys are what the search box matches
 * against, so unifying either way would silently change which items a scan or a typed SKU
 * finds. Mobile also keeps a `console.log` web never had — left in place there, because it is
 * not the only difference (rule 2(a) does not fire).
 *
 * Returns `any` for the same reason `arrayToMap` does: the app source is plain `.js`.
 */
export function itemsMidMapForBarcode(itemsData: any): any {
  let returnObj: any = {};
  if (!itemsData || (itemsData && !Object.keys(itemsData)?.length)) {
    return {};
  }
  Object.keys(itemsData).map((keyId) => {
    let uniqueKey = `${itemsData[keyId]?.info?.shortName || ""} ${getItemAttributes(itemsData[keyId]?.variantValues) || ""}_${itemsData[keyId]?.info?.longName || ""} ${(getItemAttributes(itemsData[keyId]?.variantValues) || "")}_#${itemsData[keyId]?.info?.skuCode || ""}#_%${itemsData[keyId]?.info?.barCode || ""}%`;
    uniqueKey = uniqueKey.toLowerCase();
    returnObj[uniqueKey] = itemsData[keyId].mid || "";
  });
  return returnObj;
}

/**
 * WEB-ONLY — resolves each option set to the ONE variant applicable to this branch+channel,
 * prices its options, and returns the result keyed by `mid`. Read by web `ItemsSlice`
 * (`getOptionMapData` -> `optionMapData`). Store-free: every input arrives on `params`.
 */
export function populateOptionSetVariantWrapper(params: any): any {
  const channelDefId = params?.channelDefId, branchId = params?.branchId, branchGroupId = params?.branchGroupId;
  const optionSets = params?.optionSetData;
  const itemsMap = params?.itemsData;

  const updatedOptionSets = populateOptionSetVariant(optionSets, branchId, channelDefId, branchGroupId, itemsMap);
  return arrayToMap(updatedOptionSets, "mid");
}

// this functions is taken from RL.saleutil
const populateOptionSetVariant = function (optionSets: any, branchId: any, channelDefId: any, branchGroupId: any, itemsMap: any) {
    let updatedOptionSets = structuredClone(optionSets) || [];
    itemsMap = itemsMap || {};
    updatedOptionSets.forEach(function (optionSet: any) {
      var specificVariant: any, branchVariant: any, channelVariant: any, defaultVariant: any, selectedVariant: any;
      if (optionSet.variants && optionSet.variants.length > 0) {
        optionSet.variants.forEach(function (variant: any) {
          //If branch & channel variant present
          if ((variant.branchIds && variant.branchIds.length > 0) &&
            (variant.channelDefIds && variant.channelDefIds.length > 0)) {
            if ((variant.branchIds.indexOf(branchId) !== -1) &&
              (variant.channelDefIds.indexOf(channelDefId) !== -1)) {
              specificVariant = variant;
            }
          } else if ((variant.branchIds && variant.branchIds.length > 0) &&
            (!variant.channelDefIds || variant.channelDefIds.length === 0)) {
            //branch - all channel variant
            if (variant.branchIds.indexOf(branchId) !== -1) {
              branchVariant = variant;
            }
          } else if ((!variant.branchIds || variant.branchIds.length === 0) &&
            (variant.channelDefIds && variant.channelDefIds.length > 0)) {
            //all branch - channel variant
            if (variant.channelDefIds.indexOf(channelDefId) !== -1) {
              channelVariant = variant;
            }
          } else if ((!variant.branchIds || variant.branchIds.length === 0) &&
            (!variant.channelDefIds || variant.channelDefIds.length === 0)) {
            //all branch - all channel variant
            defaultVariant = variant;
          }
        });
        selectedVariant = specificVariant || branchVariant || channelVariant || defaultVariant;
        //clone it
        selectedVariant = structuredClone(selectedVariant);

        //include only applicable prices
        if (selectedVariant) {
          var applicableOptions: any[] = [];
          selectedVariant.options.forEach(function (option: any) {
            // check if option is deactivated for branch
            if (option?.deactivationList && Array.isArray(option?.deactivationList)) {
              const isDeactivated = option?.deactivationList.some((obj: any) => {
                // Check branchId match
                const branchMatch = obj?.branchIds && obj?.branchIds.includes(branchId);
                // Check channelDefId logic
                const channelMatch =
                  obj?.channelDefId === null || obj?.channelDefId === channelDefId;
                return branchMatch && channelMatch;
              });
              if (isDeactivated) {
                return;
              }
            }
            if (selectedVariant.discountValue === undefined) {
              //Applicable only if price is specified
              if (option.price !== undefined) {
                applicableOptions.push(option);
              }
              return;
            }
            //If discounted value is provided - populate item price - discount value
            if (option.itemId) {
              var selectedPrice;
              var item = itemsMap[option.itemId];
              if (item && item.priceInfo && item.priceInfo.prices && item.priceInfo.prices.length > 0) {
                for (var i = 0; i < item.priceInfo.prices.length; i++) {
                  var priceInfo = item.priceInfo.prices[i];
                  if (priceInfo.channelDefId === channelDefId &&
                    (!branchGroupId || priceInfo.groupId === branchGroupId) && // TODO: branchGroupId is optional for now.
                    !priceInfo.scheduleId) {
                    selectedPrice = priceInfo.price;
                    break;
                  }
                }
              }
              if (selectedPrice !== undefined) {
                var clonedOption = structuredClone(option);
                clonedOption.price = common?.round2?.(selectedPrice - selectedVariant.discountValue);
                if (clonedOption.price < 0) {
                  clonedOption.price = 0;
                }
                applicableOptions.push(clonedOption);
              }
            }
          });
          selectedVariant.options = applicableOptions;
        }
      }
      optionSet.selectedVariant = selectedVariant;
    });
    return updatedOptionSets;
  };

/**
 * Identical in both apps, so they live in `shared.ts`; re-exported here because this folder's
 * variants expose a SELECTIVE surface (there is no `export * from "./shared"`), and consumers
 * import the FOLDER, not the shared file.
 */
/**
 * Whether an item has a price for the requested channel.
 *
 * 🔴 **Divergent from `index.native.ts` in the FIELD IT MATCHES ON, which is a real semantic
 * difference, not formatting.** This variant reads `params.data.channelId` and compares
 * `priceObj.channelId`; the native variant reads `channelDefId` on both sides. They are not
 * interchangeable — a channel id and a channel-definition id are different identifiers.
 * Preserved exactly as each app has it.
 *
 * ⚠️ Returns the FOUND PRICE OBJECT (truthy) or `undefined`/`""`, not a boolean, and callers
 * rely on truthiness. Preserved.
 */
export function isItemValidForChannel(params: any): any {
  const prices = params.item.priceInfo.prices;
  return (
    params?.data?.channelId &&
    prices.length > 0 &&
    prices.find((priceObj: any) => priceObj.channelId === params?.data?.channelId)
  );
}

/**
 * Reduce the catalogue map to the categories actually present, trimming each category's
 * subcategories to those present too.
 *
 * ⚠️ Divergent from the native variant. Preserved verbatim, **including the two `console.log`
 * calls** — they are the app's own and this is the web path, so removing them would be a
 * behaviour change on a platform that has always logged them.
 *
 * ⚠️ `if (!categories) return {}` can never fire: `Object.keys` always returns an array. Note
 * the return type also disagrees with the `arr` returned below (object vs array). The app's own;
 * preserved.
 */
export function parseCatagories(params: any): any {
  let arr: any[] = [];

  const categories = Object.keys(params.catalogMapData);
  console.log("parseCatagories: ", categories);
  if (!categories) return {};
  categories.forEach((catId: any) => {
    if (catId && catId !== "all") {
      const catData = params.payload?.categoryData[catId];
      const catItemObj = params.catalogMapData[catId];
      const subcatArr = catItemObj?.subCategories ? Object.keys(catItemObj?.subCategories) : [];
      let temp: any = { ...catData };

      delete temp.subCategories;
      if (subcatArr.length > 0) {
        temp.subCategories = catData?.subCategories?.filter((subCat: any) =>
          subcatArr.includes(subCat.mid)
        );
      }
      arr.push(temp);
    }
  });
  console.log("parseCatagories end: ", arr);
  return arr;
}

/**
 * Build `menuId -> { categories: [...] }` from the items map, skipping items with no menus or
 * no price on the current channel.
 *
 * ⚠️ Divergent from the native variant, and it depends on THIS file's `isItemValidForChannel`
 * (the `channelId` one) — which is precisely why it cannot be shared.
 *
 * ⚠️ The outer `let menuMap;` is declared, never assigned, and shadowed by the reduce's own
 * parameter. Dead but harmless; preserved rather than tidied.
 */
export function parseMenu(data: any): any {
  const items: any[] = Object.values(data.itemsData);
  let menuMap;

  return items?.reduce((menuMap: any, item: any) => {
    const categoryId = item.info.categoryId;
    const menuIds = item.info.menuIds;
    if (!menuIds || menuIds.length === 0) return menuMap;
    if (!isItemValidForChannel({ item, data })) return menuMap;
    menuIds.forEach((menuId: any) => {
      if (menuMap[menuId]) {
        if (!menuMap[menuId].categories) {
          menuMap[menuId].categories = [categoryId];
        } else if (!menuMap[menuId].categories.includes(categoryId)) {
          menuMap[menuId].categories.push(categoryId);
        }
      } else {
        menuMap[menuId] = {
          categories: [categoryId],
        };
      }
    });
    return menuMap;
  }, {});
}

export {
  flatItemsFromCategories,
  isDisplayOutOfStockItem,
  getOptionVariantForChannel,
  setPriorityForItemTags,
  getChannelData,
  // Adopted: was authored in this file, now shared so BOTH platforms expose it.
  getItemAttributes,
  // Pulled up by the MyOrderCard dependency closure; both apps re-export it from here.
  getAggregatorLogo,
  checkTag,
  getImageUrl,
  getVideoCdnUrl,
  getPaymentIconsUrl,
} from "./shared";

/**
 * The CATALOGUE PROJECTION lives in `catalog` — ONE pipeline shared by the POS, the
 * kiosk and recommendations, with the POS's fat card shape supplied as a `CardBuilder`
 * (`catalog/posCard`). Re-exported here so every existing web importer of
 * `@rista/core/helpers/catalogueHelpers` is unchanged, and so there is exactly one definition
 * of each.
 *
 * It briefly lived in a `packages/core/catalog` folder of its own (the Suggestive Selling
 * uplift); that folder was retired 2026-09-04 because it had become a second copy of
 * `catalog`'s resolve loop, price, schedule and naming rules.
 *
 * 🔴 `index.native.ts` deliberately does NOT re-export these: mobile's own
 * `getItemDataForCatalog` / `getItemPrice` are DIFFERENT functions with different signatures,
 * still serving mobile's catalogue parsers. See `catalog/posCard`'s header.
 */
export {
  // `getItemAttributes` is exported from `./shared` above — the same `attributeSummary`.
  getItemPrice,
  getItemDataForCatalog,
  isItemAvailableInSchedule,
  getCatalogItemsFromIds,
  DEFAULT_FILTER_CONFIG,
} from "../../catalog/posCard";
export type {
  // `CatalogContext` was this type's name while the projection lived in `packages/core/catalog`;
  // it is `CatalogContextInput` now, and lives in `catalog/context` rather than beside
  // the legacy card. Aliased so the app-side name is unchanged.
  CatalogContextInput as CatalogContext,
  CatalogItemsFilterConfig,
} from "../../catalog/posCard";

/**
 * Load (or reload) the whole catalogue for this terminal.
 *
 * Two completely different paths, which is why this is a per-variant body and `shared.ts` holds
 * nothing for it:
 * - **offline** — fire the eight `Fn` fetchers directly, so each reads its own slice out of the
 *   local store. The five in the `setTimeout` are deliberately deferred ~2s behind the first
 *   three; that ordering and delay are the app's own and are preserved verbatim.
 * - **online** — dispatch ONE action and let the saga fan out.
 *
 * ⚠️ `hideLoader` is `false` here — the offline reload is the one catalogue path that SHOWS the
 * global loader. Preserved from the app file exactly; do not "tidy" it to the fetchers' default.
 *
 * ⚠️ Guarded on `businessData` being populated AND (a stored auth token OR offline). A terminal
 * with no business data must not fetch, and the guard's operator precedence is the app's own:
 * the `&&` chain binds tighter than the inner `||`, so offline mode bypasses only the TOKEN
 * check, never the businessData one.
 *
 * ⚠️ Deep-imports the `.desktop` variants (hazard 6): `getItemsFn` and friends take this
 * platform's loader flag, and `getLocalStorage` diverges — see `utils/localStorage`.
 */
export function fetchCatalogCompleteData(isRefetchingCatalog = false) {
  const businessData = store.getState().businessStore?.businessData;
  if (
    businessData &&
    Object.keys(businessData).length &&
    (getLocalStorage(LOCALSTORAGE_KEYS.aToken) || isOffline())
  ) {
    if (isOffline()) {
      let hideLoader = false;
      getCatalogueDataFn(hideLoader);
      getItemsFn(hideLoader);
      getOptionSetsFn(hideLoader);
      setTimeout(() => {
        getDiscountsFn(hideLoader);
        getAllTablesFn(hideLoader);
        getResourceFn(hideLoader);
        getResourceGroupsFn(hideLoader);
        getPrintersFn(hideLoader);
      }, 2000);
    } else if (getLocalStorage(LOCALSTORAGE_KEYS.aToken)) {
      store.dispatch(
        fetchCompleteCatalogData({isRefetchingCatalog})
      );
    }
  }
}

/**
 * Whether the most-sold-items list is stale enough to refetch.
 *
 * ⚠️ **WEB-ONLY** — the most-sold feature and its two state fields exist only on this platform's
 * `catalogueSlice` (`mostSoldItems`, `mostSoldLastFetchingTime`), so there is nothing to lift on
 * the other side.
 *
 * ⚠️ `new Date("")` is `Invalid Date`, so on a terminal that has never fetched, `timeDifference`
 * is `NaN` and `NaN >= MILI_SECS_IN_ONE_DAY` is FALSE — the refetch is driven by the
 * `!mostSoldItems?.length` half of the `||` instead. Preserved exactly; the two halves are not
 * redundant.
 */
export function needsMostSoldItemsFetch() {
  const mostSoldLastFetchingTime =
    store.getState().catalogStore.mostSoldLastFetchingTime || "";
  const mostSoldItems = store.getState().catalogStore.mostSoldItems || [];
  const currentDate: any = new Date();
  const mostSoldLastFetchTime: any = new Date(mostSoldLastFetchingTime);
  const timeDifference = Math.abs(mostSoldLastFetchTime - currentDate);

  // Check if we need to fetch most sold items
  return timeDifference >= MILI_SECS_IN_ONE_DAY || !mostSoldItems?.length;
}

/*
 * ⚠️ `getImageUrl` USED TO HAVE A BODY HERE, and a second one in `index.native.ts`. It does not
 * any more — it is one function in `./shared.ts`, re-exported above.
 *
 * The two bodies differed in a single expression: web read
 * `process.env.REACT_APP_RISTA_ITEM_BASE_URL` (webpack inlines it, so shared code cannot),
 * mobile read the effects bridge. That is a bridge key, not a platform split — web wires
 * `ristaItemBaseUrl` too as of 2026-09-09 and the one body reads it on both platforms. Do not
 * re-add a variant body for a value the bridge can carry.
 */
